import { useState, useEffect } from "react";
import { Button, useDisclosure } from "@heroui/react";
import { Plus } from "@phosphor-icons/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";

// Import components and types
import {
  EditDocumentTemplateModal,
  PreviewDocumentTemplateModal,
  DeleteDocumentTemplateModal,
  DocumentTemplatesTable,
  DocumentTemplateInstructions,
  DocumentTemplate,
} from "./components/document-templates";
import CreateDocumentTemplateModalEntityBased from "./components/document-templates/CreateDocumentTemplateModalEntityBased";

const DocumentTemplatesTab = () => {
  const { session } = useSession();

  // State
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] =
    useState<DocumentTemplate | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Modal controls
  const {
    isOpen: isCreateModalOpen,
    onOpen: onCreateModalOpen,
    onClose: onCreateModalClose,
  } = useDisclosure();
  const {
    isOpen: isEditModalOpen,
    onOpen: onEditModalOpen,
    onClose: onEditModalClose,
  } = useDisclosure();
  const {
    isOpen: isPreviewModalOpen,
    onOpen: onPreviewModalOpen,
    onClose: onPreviewModalClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteModalOpen,
    onOpen: onDeleteModalOpen,
    onClose: onDeleteModalClose,
  } = useDisclosure();

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setIsLoading(true);
      const response = await fetchWithAuth(
        `${API_URL}/document-generation/templates`,
        { method: "GET" },
        session,
      );

      if (response.ok) {
        const fetchedTemplates = await response.json();
        setTemplates(fetchedTemplates);
      }
    } catch (error) {
      console.error("Error fetching document templates:", error);
      toast.error("Fout bij het ophalen van document sjablonen");
    } finally {
      setIsLoading(false);
    }
  };

  // Template handlers
  const handlePreviewTemplate = (template: DocumentTemplate) => {
    setSelectedTemplate(template);
    onPreviewModalOpen();
  };

  const handleEditTemplate = (template: DocumentTemplate) => {
    setSelectedTemplate(template);
    onEditModalOpen();
  };

  const handleDeleteTemplate = (template: DocumentTemplate) => {
    setSelectedTemplate(template);
    onDeleteModalOpen();
  };

  const handleTemplateSuccess = () => {
    fetchTemplates();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-neutral-primary text-xl font-semibold">
            Document sjablonen
          </h2>
          <p className="text-neutral-secondaryMid text-sm">
            Beheer je Word document sjablonen voor verschillende doeleinden
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            startContent={<Plus size={16} />}
            className="bg-moss-primary text-white"
            onPress={onCreateModalOpen}
          >
            Nieuw sjabloon
          </Button>
        </div>
      </div>

      {/* Instructions Section */}
      <DocumentTemplateInstructions />

      {/* Templates Table */}
      <div className="border-neutral-primary/10 rounded-lg border bg-white">
        <DocumentTemplatesTable
          templates={templates}
          isLoading={isLoading}
          onPreview={handlePreviewTemplate}
          onEdit={handleEditTemplate}
          onDelete={handleDeleteTemplate}
        />
      </div>

      {/* Modals */}
      <CreateDocumentTemplateModalEntityBased
        isOpen={isCreateModalOpen}
        onClose={onCreateModalClose}
        onSuccess={handleTemplateSuccess}
      />

      <EditDocumentTemplateModal
        isOpen={isEditModalOpen}
        onClose={onEditModalClose}
        template={selectedTemplate}
        onSuccess={handleTemplateSuccess}
      />

      <PreviewDocumentTemplateModal
        isOpen={isPreviewModalOpen}
        onClose={onPreviewModalClose}
        template={selectedTemplate}
      />

      <DeleteDocumentTemplateModal
        isOpen={isDeleteModalOpen}
        onClose={onDeleteModalClose}
        template={selectedTemplate}
        onSuccess={handleTemplateSuccess}
      />
    </div>
  );
};

export default DocumentTemplatesTab;
